import type { ProviderListItem } from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ProviderAvatar } from '@/components/provider-avatar';
import type { Column } from '@/components/ui/data-table';
import { DataTable } from '@/components/ui/data-table';
import { Pagination } from '@/components/ui/pagination';
import { DataTableSkeleton, EmptyState, ErrorState, LoadingOverlay } from '@/components/ui/state-views';
import { searchProviders } from '@/config/api-helpers';
import { useDebounced } from '@/hooks/useDebounced';
import { formatDate } from '@/utils/format';

const DEFAULT_PAGINATION = { page: 1, pageSize: 25, totalCount: 0, totalPages: 1 };

const columns: Column<ProviderListItem>[] = [
  {
    key: 'provider',
    header: 'Provider',
    render: (p) => (
      <div className='flex items-center gap-3'>
        <ProviderAvatar firstName={p.firstName} lastName={p.lastName} photoUrl={p.photoUrl} />
        <div>
          <div className='font-medium text-neutral-900'>
            {p.firstName} {p.lastName}
          </div>
          <div className='text-neutral-500'>{p.email}</div>
        </div>
      </div>
    ),
  },
  { key: 'providerId', header: 'Provider ID', render: (p) => <span className='text-neutral-700'>{p.providerId}</span> },
  { key: 'userId', header: 'User ID', render: (p) => <span className='text-neutral-700'>{p.userId}</span> },
  { key: 'type', header: 'Type', render: (p) => <span className='text-neutral-700'>{p.type}</span> },
  {
    key: 'status',
    header: 'Status',
    render: (p) => <span className='text-neutral-700'>{p.isActive ? 'Active' : 'Inactive'}</span>,
  },
  { key: 'tag', header: 'Tier', render: (p) => <span className='text-neutral-700'>{p.tag ?? '\u2014'}</span> },
  { key: 'state', header: 'State', render: (p) => <span className='text-neutral-700'>{p.state ?? '\u2014'}</span> },
  {
    key: 'startDate',
    header: 'Start Date',
    render: (p) => <span className='text-neutral-700'>{formatDate(p.startDate)}</span>,
  },
];

export function ProviderList() {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const query = useDebounced(searchQuery, 300);

  // Reset to page 1 when the search query changes
  useEffect(() => setPage(1), [query]);

  const { data, error, isFetching, isLoading, refetch } = useQuery({
    queryKey: ['providers', query, page],
    queryFn: () => searchProviders({ query, page, pageSize: 25 }),
  });

  const items = data?.items ?? [];
  const pagination = data?.pagination ?? DEFAULT_PAGINATION;

  const pageSummary = useMemo(() => {
    if (pagination.totalCount === 0) return 'No providers found';
    const start = (pagination.page - 1) * pagination.pageSize + 1;
    const end = Math.min(start + items.length - 1, pagination.totalCount);
    return `Showing ${start}\u2013${end} of ${pagination.totalCount} providers`;
  }, [items.length, pagination.page, pagination.pageSize, pagination.totalCount]);

  const isInitialLoading = isLoading && items.length === 0 && !error;
  const showOverlay = isFetching && items.length > 0;
  const hasLoadedResults = data != null && !isInitialLoading && !error;

  return (
    <div className='space-y-6'>
      <h2 className='font-mediumbold text-xl text-neutral-900'>Providers</h2>

      {error && (
        <ErrorState message={error?.message ?? ''} onRetry={() => refetch()} title='Providers are unavailable' />
      )}

      <input
        className='w-full rounded-md border border-neutral-200 px-3 py-2 text-sm text-neutral-900 focus:border-neutral-400 focus:outline-none'
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder='Search by name, email, provider ID, or user ID'
        value={searchQuery}
      />

      {isInitialLoading ? (
        <div className='rounded-lg border border-neutral-200 bg-white'>
          <DataTableSkeleton columns={columns.length} label='Loading providers' />
        </div>
      ) : hasLoadedResults ? (
        <div className='relative rounded-lg border border-neutral-200 bg-white'>
          <div className='flex items-center justify-between border-b border-neutral-100 px-4 py-3'>
            <p className='text-sm text-neutral-600'>{pageSummary}</p>
            <p className='text-sm text-neutral-500'>Page {pagination.page}</p>
          </div>

          {items.length === 0 ? (
            <EmptyState message='No providers match this query.' />
          ) : (
            <DataTable
              columns={columns}
              data={items}
              keyFn={(p) => p.providerId}
              onRowClick={(p) => navigate(`/providers/${p.providerId}`)}
            />
          )}

          {showOverlay && <LoadingOverlay label='Refreshing providers' />}

          <Pagination
            page={pagination.page}
            totalPages={pagination.totalPages}
            onPageChange={setPage}
            disabled={isLoading}
          />
        </div>
      ) : null}
    </div>
  );
}
